import { CartAddEvent, ThemeEvents } from '@theme/events';

class DuoSelector extends HTMLElement {
  #variants = [];
  #controller;
  #busy = false;
  #navigating = false;
  #nativeButtons;
  #currentVariantId;
  #buttonText = '';

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    try {
      this.#variants = JSON.parse(this.querySelector('[data-duo-variants]')?.textContent || '[]');
      if (!Array.isArray(this.#variants) || !this.#variants.length) return;
    } catch (error) {
      console.error('[duo-selector] Invalid variant data', error);
      return;
    }

    this.scope = this.closest('.product-details') || this.closest('.shopify-section');
    if (!this.scope) return;
    this.form = Array.from(this.scope.querySelectorAll('product-form-component')).find(
      (form) => form.dataset.productId === this.dataset.productId
    );
    this.#nativeButtons = this.form?.closest('.buy-buttons-block');
    this.radios = Array.from(this.querySelectorAll('[data-duo-mode]'));
    this.selects = Array.from(this.querySelectorAll('[data-duo-variant]'));
    this.addButton = this.querySelector('[data-duo-add]');
    this.buttonText = this.querySelector('[data-duo-button-text]');
    this.errorElement = this.querySelector('[data-duo-error]');
    this.statusElement = this.querySelector('[data-duo-status]');
    if (!this.#nativeButtons || !this.addButton || this.selects.length !== 2 || !this.radios.length) return;

    this.#buttonText = this.buttonText?.textContent?.trim() || this.addButton.textContent.trim();
    this.#currentVariantId = this.#selectedVariantId() || this.dataset.initialVariantId;

    for (const input of [...this.radios, ...this.selects]) {
      input.addEventListener('change', () => {
        this.#clearMessages();
        this.#render();
      }, { signal });
    }
    this.addButton.addEventListener('click', () => this.#add(), { signal });
    this.scope.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.form.querySelector('input[name="id"]')?.addEventListener('change', this.#onVariantInput, { signal });
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      this.#busy = false;
      this.#navigating = false;
      this.#render();
    }, { signal });

    this.dataset.ready = 'true';
    this.#render();
    this.hidden = false;
  }

  disconnectedCallback() {
    this.#nativeButtons?.classList.remove('cz-duo-native-hidden');
    this.#controller?.abort();
    delete this.dataset.ready;
  }

  #selectedVariantId() {
    return this.form?.querySelector('input[name="id"]')?.value;
  }

  #variant(id) {
    return this.#variants.find((variant) => String(variant.id) === String(id));
  }

  #onVariantInput = () => this.#syncFirstVariant(this.#selectedVariantId());

  #onVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
    const variant = event.detail?.resource;
    if (variant?.id) this.#syncFirstVariant(variant.id);
  };

  #syncFirstVariant(id) {
    if (!this.#variant(id)) return;
    this.#currentVariantId = String(id);
    this.selects[0].value = String(id);
    this.#clearMessages();
    this.#render();
  }

  get mode() {
    return this.radios.find((radio) => radio.checked)?.value || 'single';
  }

  #money(cents) {
    const currency = this.dataset.currency || 'USD';
    try {
      return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(cents / 100);
    } catch {
      return `${currency} ${(cents / 100).toFixed(2)}`;
    }
  }

  #setText(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value;
  }

  #pair() {
    return this.selects.map((select) => this.#variant(select.value));
  }

  #canAddPair() {
    const [first, second] = this.#pair();
    if (!first?.available || !second?.available) return false;
    for (const variant of [first, second]) {
      const needed = String(first.id) === String(second.id) ? 2 : 1;
      if (variant.max_quantity != null && Number(variant.max_quantity) < needed) return false;
    }
    return true;
  }

  #render() {
    const isDuo = this.mode === 'duo';
    this.dataset.mode = this.mode;
    this.#nativeButtons?.classList.toggle('cz-duo-native-hidden', isDuo);

    const single = this.#variant(this.#currentVariantId);
    if (single) {
      this.#setText('[data-duo-single-price]', this.#money(single.price));
      const compare = this.querySelector('[data-duo-single-compare]');
      if (compare) {
        compare.hidden = !(single.compare_at_price > single.price);
        compare.textContent = compare.hidden ? '' : this.#money(single.compare_at_price);
      }
    }

    const [first, second] = this.#pair();
    const full = Number(first?.price || 0) + Number(second?.price || 0);
    const discounted = full - Math.round(Math.min(Number(first?.price || 0), Number(second?.price || 0)) / 2);
    this.#setText('[data-duo-price]', this.#money(discounted));
    const compare = this.querySelector('[data-duo-compare]');
    if (compare) {
      compare.textContent = this.#money(full);
      compare.hidden = full <= discounted;
    }

    this.addButton.hidden = !isDuo;
    this.addButton.disabled = this.#busy || !this.#canAddPair();
    for (const input of [...this.radios, ...this.selects]) input.disabled = this.#busy;
    if (this.buttonText) this.buttonText.textContent = this.#busy
      ? (this.dataset.addingText || 'Adding…')
      : this.#buttonText;
    if (this.#busy) this.addButton.setAttribute('aria-busy', 'true');
    else this.addButton.removeAttribute('aria-busy');

    if (isDuo && !this.#canAddPair()) {
      this.#showError(this.dataset.stockErrorText || 'This combination is unavailable. Please choose another color.');
    }
  }

  #clearMessages() {
    if (this.errorElement) {
      this.errorElement.hidden = true;
      this.errorElement.textContent = '';
    }
    if (this.statusElement) this.statusElement.textContent = '';
  }

  #showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  #cartUrl() {
    return this.dataset.cartUrl || window.Theme?.routes?.cart_url || '/cart';
  }

  #goToCart() {
    this.#navigating = true;
    window.location.assign(this.#cartUrl());
  }

  async #refreshRejectedCart() {
    try {
      const response = await fetch(`${this.#cartUrl()}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      if (!Number.isFinite(cart.item_count)) return;
      document.dispatchEvent(new CartAddEvent(cart, this.id, {
        source: 'duo-selector',
        itemCount: cart.item_count,
        productId: this.dataset.productId,
        didError: true,
      }));
    } catch {}
  }

  async #add() {
    if (this.#busy || this.mode !== 'duo' || !this.#canAddPair()) return;
    this.#busy = true;
    this.#clearMessages();
    this.#render();

    const counts = new Map();
    for (const variant of this.#pair()) {
      counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
    }
    const items = Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));
    const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const body = { items, sections_url: window.location.pathname };
    if (sections.length) body.sections = sections;

    let rejected = false;
    try {
      const response = await fetch(this.dataset.cartAddUrl || window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        rejected = true;
        const message = typeof data.description === 'string' ? data.description : data.message;
        this.#showError(typeof message === 'string' ? message : (this.dataset.errorText || 'Unable to add these items.'));
        await this.#refreshRejectedCart();
        return;
      }
      if (!Array.isArray(data.items)) throw new Error('Unexpected cart response');
      if (this.statusElement) this.statusElement.textContent = this.dataset.addedText || 'Both pairs were added to your cart.';

      document.dispatchEvent(new CartAddEvent(data, this.id, {
        source: 'product-form-component',
        itemCount: 2,
        productId: this.dataset.productId,
        sections: data.sections,
      }));

      const drawer = document.querySelector('cart-drawer-component');
      const drawerSection = drawer?.querySelector('cart-items-component')?.dataset.sectionId;
      if (!drawer || !drawerSection || !data.sections?.[drawerSection]) {
        this.#goToCart();
      }
    } catch (error) {
      console.error('[duo-selector]', error);
      if (!rejected) this.#goToCart();
    } finally {
      this.#busy = this.#navigating;
      if (this.isConnected) this.#render();
    }
  }
}

if (!customElements.get('duo-selector')) customElements.define('duo-selector', DuoSelector);
